// React, Next
import { useContext } from 'react'
import { useTranslation } from 'next-i18next'
import { useRouter } from 'next/router'
import Link from 'next/link'

// MUI
import Box from '@mui/material/Box'
import Button from '@mui/material/Button'
import Typography from '@mui/material/Typography'
import ArrowForwardIosIcon from '@mui/icons-material/ArrowForwardIos'

// Custom
import Section from '@/components/sections/Section'
import { translateHref } from '@/assets/functions/links'
import { GlobalContext } from 'context/GlobalContextProvider'

// Config
import nextI18nextConfig from 'next-i18next.config'

// Style
import themeColors from 'assets/theme/colors'
import breakpoints from 'assets/theme/breakpoints'
import classes from './AboutView-CSS'

export default function AboutView() {
  const { t } = useTranslation('about')
  const { screenDimensions } = useContext(GlobalContext)

  const router = useRouter()
  const currentLocale =
    router.query.locale || nextI18nextConfig.i18n.defaultLocale

  const wide = screenDimensions.width > breakpoints.mobileSmall

  const beliefs = [
    {
      title: t('beliefs.items.01.title'),
      description: t('beliefs.items.01.description')
    },
    {
      title: t('beliefs.items.02.title'),
      description: t('beliefs.items.02.description')
    },
    {
      title: t('beliefs.items.03.title'),
      description: t('beliefs.items.03.description')
    },
    {
      title: t('beliefs.items.04.title'),
      description: t('beliefs.items.04.description')
    }
  ]

  const heading = (text: string) => (
    <Typography
      variant="h2"
      sx={classes.h2}
      style={{ fontSize: wide ? '1.5rem' : '1.375rem' }}
    >
      {text}
    </Typography>
  )

  const paragraph = (text: string) => (
    <Typography key={text} component="p" sx={classes.b1}>
      {text}
    </Typography>
  )

  const getContent = () => (
    <Box sx={classes.sndDiv}>
      <Typography
        variant="h1"
        sx={classes.h1}
        style={{ fontSize: wide ? '2.125rem' : '1.875rem' }}
      >
        {t('h1')}
      </Typography>

      <Typography
        component="p"
        sx={classes.tagline}
        style={{ fontSize: wide ? '1.25rem' : '1.125rem' }}
      >
        {t('tagline')}
      </Typography>

      <Box component="section">
        {[t('intro.p1'), t('intro.p2'), t('intro.p3')].map(paragraph)}
      </Box>

      <Box component="section">
        {heading(t('clients.h2'))}
        {[t('clients.p1'), t('clients.p2'), t('clients.p3')].map(paragraph)}
      </Box>

      <Box component="section">
        {heading(t('beliefs.h2'))}
        {beliefs.map(belief => (
          <Box key={belief.title} sx={classes.beliefBox}>
            <Typography component="p" sx={classes.beliefTitle}>
              {belief.title}
            </Typography>
            <Typography component="p" sx={classes.b1}>
              {belief.description}
            </Typography>
          </Box>
        ))}
      </Box>

      <Box component="section">
        {heading(t('who.h2'))}
        {paragraph(t('who.p1'))}
      </Box>

      <Box sx={classes.companyBox}>{paragraph(t('company'))}</Box>

      <Box sx={classes.ctaBox}>
        <Typography
          variant="h2"
          sx={classes.ctaTitle}
          style={{ fontSize: wide ? '1.35rem' : '1.2rem' }}
        >
          {t('cta.title')}
        </Typography>

        <Link
          href={translateHref(currentLocale, 'contact')}
          style={{ textDecoration: 'none' }}
        >
          <Button
            variant="contained"
            endIcon={<ArrowForwardIosIcon sx={{ transform: 'scale(0.7)' }} />}
            sx={{
              background: themeColors.success.main,
              borderRadius: '4px',
              padding: '0.7rem 1.4rem',
              fontWeight: 600,
              letterSpacing: '0.03em',
              boxShadow: 'none',
              '&:hover': {
                background: themeColors.success.darker,
                boxShadow: 'none'
              }
            }}
          >
            {t('cta.button')}
          </Button>
        </Link>
      </Box>
    </Box>
  )

  // *** MAIN RENDER ***
  return (
    <Section paddingTop="4rem" paddingBottom="5rem" content={getContent()} />
  )
}
